// frontend/src/components/RouterEngine.js
// --- VERSIÓN 9.0: ESTRATEGIA DE "PUENTES INVISIBLES" (MÁXIMA CONECTIVIDAD) ---

// 1. CORRECCIÓN DE COORDENADAS
function fixCoord(coord) {
    if (!Array.isArray(coord)) return null;
    let [c1, c2] = coord.map(parseFloat);
    // Si la Latitud es < 10 y Longitud > 30 (España), están al revés. Girar.
    if (Math.abs(c1) < 10 && Math.abs(c2) > 30) return [c2, c1];
    return [c1, c2];
}

function getDistance(p1, p2) {
    const R = 6371e3; 
    const c1 = fixCoord(p1);
    const c2 = fixCoord(p2);
    if(!c1 || !c2) return Infinity;

    const lat1 = c1[0] * Math.PI / 180;
    const lat2 = c2[0] * Math.PI / 180;
    const deltaLat = (c2[0] - c1[0]) * Math.PI / 180;
    const deltaLng = (c2[1] - c1[1]) * Math.PI / 180;

    const a = Math.sin(deltaLat / 2) * Math.sin(deltaLat / 2) +
              Math.cos(lat1) * Math.cos(lat2) *
              Math.sin(deltaLng / 2) * Math.sin(deltaLng / 2);
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

// 2. CONSTRUCCIÓN DEL GRAFO
function buildGraph(tramos) {
    const graph = {}; // { "ID": { coords: [], neighbors: [] } }
    const allNodes = []; // Lista para buscar vecinos

    // Helper para añadir nodo si no existe
    const addNode = (coord) => {
        const c = fixCoord(coord);
        // Usamos la coordenada exacta como ID único
        const id = `${c[0].toFixed(6)},${c[1].toFixed(6)}`;
        if (!graph[id]) {
            graph[id] = { id: id, coords: c, neighbors: [] };
            allNodes.push(graph[id]);
        }
        return id;
    };

    // Helper para conectar dos nodos
    const addEdge = (idA, idB, weight) => {
        if (idA === idB) return;
        // Evitamos duplicados
        if (!graph[idA].neighbors.some(n => n.node === idB)) {
            graph[idA].neighbors.push({ node: idB, weight: weight });
        }
        if (!graph[idB].neighbors.some(n => n.node === idA)) {
            graph[idB].neighbors.push({ node: idA, weight: weight });
        }
    };

    // FASE A: Convertir calles reales en nodos y conexiones
    tramos.forEach(tramo => {
        let rawCoords = tramo.polilinea_coords;
        if (typeof rawCoords === 'string') try { rawCoords = JSON.parse(rawCoords); } catch(e){}

        if (Array.isArray(rawCoords) && rawCoords.length > 1) {
            for (let i = 0; i < rawCoords.length - 1; i++) {
                const idA = addNode(rawCoords[i]);
                const idB = addNode(rawCoords[i+1]);
                const dist = getDistance(graph[idA].coords, graph[idB].coords);
                addEdge(idA, idB, dist); // Conexión física (calle)
            }
        }
    });

    // FASE B: ¡PUENTES INVISIBLES! (La Magia)
    // Conectamos cualquier nodo que esté cerca de otro, aunque no haya calle dibujada.
    // Esto permite saltar huecos de hasta 15 metros.
    const BRIDGE_TOLERANCE = 15; // Metros

    console.log(`🏗️ Analizando ${allNodes.length} puntos para crear puentes...`);
    
    // Comparación todos contra todos (Rápido para < 1000 nodos)
    for (let i = 0; i < allNodes.length; i++) {
        for (let j = i + 1; j < allNodes.length; j++) {
            const nodeA = allNodes[i];
            const nodeB = allNodes[j];
            const dist = getDistance(nodeA.coords, nodeB.coords);

            if (dist < BRIDGE_TOLERANCE) {
                // Creamos un puente con un pequeño coste extra para preferir calles reales
                addEdge(nodeA.id, nodeB.id, dist * 1.5); 
            }
        }
    }

    return graph;
}

// 3. BUSCADOR DE PUNTO DE ENTRADA
function findNearestNodeKey(target, graph) {
    let nearestKey = null;
    let minDistance = Infinity;
    const cleanTarget = fixCoord(target);
    const RADIO_MAXIMO = 100; // 100 metros de margen para encontrar una calle

    Object.values(graph).forEach(node => {
        const dist = getDistance(cleanTarget, node.coords);
        if (dist < minDistance && dist < RADIO_MAXIMO) { 
            minDistance = dist;
            nearestKey = node.id;
        }
    });
    return nearestKey;
}

// 4. EJECUTAR RUTA (DIJKSTRA)
export function findSmartPath(startCoords, endCoords, allStreets) {
    if (!allStreets || allStreets.length === 0) return null;

    const graph = buildGraph(allStreets);
    const startKey = findNearestNodeKey(startCoords, graph);
    const endKey = findNearestNodeKey(endCoords, graph);

    if (!startKey || !endKey) {
        console.warn("GPS: Inicio o Fin demasiado lejos de cualquier calle conocida.");
        return null;
    }

    const distances = {};
    const previous = {};
    const pq = new Set(Object.keys(graph));

    for (const node of pq) {
        distances[node] = Infinity;
        previous[node] = null;
    }
    distances[startKey] = 0;

    while (pq.size > 0) {
        let minNode = null;
        for (const node of pq) {
            if (minNode === null || distances[node] < distances[minNode]) minNode = node;
        }

        if (minNode === endKey || distances[minNode] === Infinity) break;
        pq.delete(minNode);

        const neighbors = graph[minNode].neighbors || [];
        neighbors.forEach(neighbor => {
            if (pq.has(neighbor.node)) {
                const alt = distances[minNode] + neighbor.weight;
                if (alt < distances[neighbor.node]) {
                    distances[neighbor.node] = alt;
                    previous[neighbor.node] = minNode;
                }
            }
        });
    }

    // Reconstruir camino
    const path = [];
    let u = endKey;
    if (previous[u] || u === startKey) {
        while (u) {
            path.unshift(graph[u].coords);
            u = previous[u];
        }
    }

    if (path.length > 0) {
        console.log(`✅ GPS: Ruta encontrada con ${path.length} pasos.`);
        // Añadimos inicio y fin exactos para que quede perfecto
        path.unshift(fixCoord(startCoords));
        path.push(fixCoord(endCoords));
        return path;
    }
    
    console.warn("⚠️ GPS: Imposible conectar. Islas separadas por más de 15m.");
    return null;
}